import { INTEGRATION_PROVIDER_LABELS, type RepositoryLinkSummary } from '@ashniva/types';
import { Card, EmptyState, Table, type TableColumn } from '@ashniva/ui';
import { Link } from 'react-router';

import { QueryState } from '../../../shared/components/QueryState';
import { useProjectLinksQuery } from '../api';
import { RepositoryStatusCell } from './RepositoryStatusCell';

import '../repository-intelligence.css';

/**
 * A project's "Code" tab (MASTER-SPEC §14.4 entry point 1, P14-02): its linked repositories with where each is with
 * Repository Intelligence and how fresh its index is, each opening the repository's workspace.
 */
export function ProjectCodePanel({ projectId }: { projectId: string }) {
  const links = useProjectLinksQuery(projectId);
  const columns: TableColumn<RepositoryLinkSummary>[] = [
    {
      key: 'repository',
      header: 'Repository',
      render: (link) => (
        <Link to={`/projects/${encodeURIComponent(projectId)}/code/${encodeURIComponent(link.id)}`}>
          {link.owner}/{link.name}
        </Link>
      ),
    },
    {
      key: 'provider',
      header: 'Host',
      hideOnMobile: true,
      render: (link) => INTEGRATION_PROVIDER_LABELS[link.provider],
    },
    {
      key: 'state',
      header: 'Repository Intelligence',
      render: (link) => <RepositoryStatusCell linkId={link.id} />,
    },
  ];
  return (
    <Card
      title="Code"
      subtitle="Search, read and ask about the code of this project’s repositories."
    >
      <QueryState
        isLoading={links.isLoading}
        isError={links.isError}
        error={links.error}
        onRetry={() => void links.refetch()}
      >
        {links.data ? (
          <Table
            aria-label="Linked repositories"
            columns={columns}
            rows={links.data}
            rowKey={(link) => link.id}
            empty={
              <EmptyState
                size="sm"
                icon="○"
                title="No repositories are linked to this project"
                description="A repository linked to the project appears here."
              />
            }
          />
        ) : null}
      </QueryState>
    </Card>
  );
}
